import type { ITableSettings } from "../types";
import { calcCellText } from "./calc-cell-text";
import {
	evaluateCalculation,
	rawCalculationResult,
} from "./evaluate-calculation";
import { getHeaderRowIndex } from "./get-header-row-index";
import { parseSortableValue } from "./parse-sortable-value";
import { makeCellResolver } from "./resolve-cell-text";
import { buildRowGroups } from "./sort-row-order";

export type ColumnFilter =
	| { kind: "values"; selected: Set<string> }
	| { kind: "range"; min: number | null; max: number | null };

export type TableFilterState = Map<number, ColumnFilter>;

const isColumnFilterActive = (f: ColumnFilter): boolean => {
	if (f.kind === "values") return true;
	return f.min !== null || f.max !== null;
};

export const hasActiveFilters = (filters: TableFilterState): boolean => {
	let active = false;
	filters.forEach((f) => {
		if (isColumnFilterActive(f)) active = true;
	});
	return active;
};

export const applyFilters = (
	rowOrder: number[],
	settings: Pick<
		ITableSettings,
		| "tableData"
		| "tableCols"
		| "tableRows"
		| "tableHasHeader"
		| "tableHeaderPosition"
	>,
	filters: TableFilterState,
): number[] => {
	if (!hasActiveFilters(filters)) return rowOrder;
	const {
		tableData,
		tableCols,
		tableRows,
		tableHasHeader,
		tableHeaderPosition,
	} = settings;
	if (!tableData || !tableCols || !tableRows) return rowOrder;

	const headerRow = getHeaderRowIndex({
		tableRows,
		tableHasHeader,
		tableHeaderPosition,
	});

	const activeCols: Array<[number, ColumnFilter]> = [];
	filters.forEach((f, col) => {
		if (isColumnFilterActive(f) && col >= 0 && col < tableCols) {
			activeCols.push([col, f]);
		}
	});
	if (activeCols.length === 0) return rowOrder;

	const { resolveText } = makeCellResolver(tableData);
	const rowMatches = (row: number): boolean => {
		for (const [col, f] of activeCols) {
			const cell = tableData[col + row * tableCols];

			// Calc cells: values filter matches the formatted result string,
			// range uses the raw numeric result. rowOrder is intentionally omitted
			// here (unlike table-rows.tsx, which passes post-filter iterationOrder)
			// to avoid a filter↔calc circular dependency; we match the table-wide value.
			let text: string;
			let numericValue: number | null = null;
			if (cell?.calculation) {
				const result = evaluateCalculation(cell.calculation.formula, {
					tableData,
					tableCols,
					tableRows,
					cellCoord: { col, row },
				});
				text = rawCalculationResult(result);
				if (result.kind === "ok") numericValue = result.value;
			} else {
				text = resolveText(cell);
			}

			if (f.kind === "values") {
				if (!f.selected.has(text)) return false;
			} else {
				const val =
					numericValue !== null
						? numericValue
						: (() => {
								const parsed = parseSortableValue(text);
								return parsed.kind === "number"
									? (parsed.value as number)
									: null;
							})();
				if (val === null) return false;
				if (f.min !== null && val < f.min) return false;
				if (f.max !== null && val > f.max) return false;
			}
		}
		return true;
	};

	const keptRows = new Set<number>();
	for (const group of buildRowGroups(tableData, tableCols, tableRows)) {
		if (
			group.includes(headerRow) ||
			group.some((r) => r !== headerRow && rowMatches(r))
		) {
			for (const r of group) keptRows.add(r);
		}
	}

	return rowOrder.filter((row) => keptRows.has(row));
};

export const applyGlobalValuesFilter = (
	rowOrder: number[],
	settings: Pick<
		ITableSettings,
		| "tableData"
		| "tableCols"
		| "tableRows"
		| "tableHasHeader"
		| "tableHeaderPosition"
	>,
	selected: Set<string>,
): number[] => {
	const {
		tableData,
		tableCols,
		tableRows,
		tableHasHeader,
		tableHeaderPosition,
	} = settings;
	if (!tableData || !tableCols || !tableRows) return rowOrder;

	const headerRow = getHeaderRowIndex({
		tableRows,
		tableHasHeader,
		tableHeaderPosition,
	});

	const { resolveText } = makeCellResolver(tableData);
	const rowMatches = (row: number): boolean => {
		for (let col = 0; col < tableCols; col++) {
			const cell = tableData[col + row * tableCols];
			const text =
				calcCellText(cell, tableData, tableCols, tableRows, col, row) ??
				resolveText(cell);
			if (!selected.has(text)) {
				return false;
			}
		}
		return true;
	};

	const keptRows = new Set<number>();
	for (const group of buildRowGroups(tableData, tableCols, tableRows)) {
		if (
			group.includes(headerRow) ||
			group.every((r) => r === headerRow || rowMatches(r))
		) {
			for (const r of group) keptRows.add(r);
		}
	}

	return rowOrder.filter((row) => keptRows.has(row));
};
